<script setup lang="ts">
import { computed } from 'vue';
import CalendarDay from './calendar-day.vue';
import { DEFAULT_LABELS, type CalendarLabels, type DayAvailability, type VenueFilter } from './types';

const { venueFilter, labels = DEFAULT_LABELS } = defineProps<{ venueFilter: VenueFilter; labels?: CalendarLabels }>();

const DAY = '2026-11-14'; // arbitrary fixture date — sample mode renders no date text

// Sample-cell props the legend drives; a dedicated type (not Record<string, any>)
// so a misspelled key fails vue-tsc instead of silently rendering wrong.
type SampleProps = {
  state: 'available' | 'unavailable';
  selected?: boolean;
  showVenueBar?: boolean;
  filter?: VenueFilter;
  availability?: DayAvailability;
};
type Row = { key: string; label: string; props: SampleProps };

const avail = (patch: Partial<DayAvailability>): DayAvailability =>
  ({ price: 'base', discount: 0, venues: ['lasalle', 'serre'], ...patch });

const rows = computed<Row[]>(() => {
  const venueRows: Row[] = venueFilter === 'both'
    ? [
        { key: 'lasalle', label: labels.legendLasalle, props: { state: 'available', showVenueBar: true, availability: avail({ venues: ['lasalle'] }) } },
        { key: 'serre', label: labels.legendSerre, props: { state: 'available', showVenueBar: true, availability: avail({ venues: ['serre'] }) } },
      ]
    : [
        // Filtered view: one row explaining the full-width bar drawn in the active venue's color.
        {
          key: venueFilter,
          label: venueFilter === 'lasalle' ? labels.legendLasalle : labels.legendSerre,
          props: { state: 'available', showVenueBar: true, filter: venueFilter, availability: avail({ venues: [venueFilter] }) },
        },
      ];
  return [
    ...venueRows,
    { key: 'selected', label: labels.legendSelected, props: { state: 'available', selected: true, availability: avail({}) } },
    { key: 'unavailable', label: labels.legendUnavailable, props: { state: 'unavailable' } },
    { key: 'deal', label: labels.legendDeal, props: { state: 'available', availability: avail({ price: 'special' }) } },
  ];
});
</script>

<template>
  <ul class="calendar-legend">
    <li v-for="row in rows" :key="row.key" class="legend-row">
      <span class="legend-swatch"><CalendarDay :day="DAY" sample v-bind="row.props" /></span>
      <span class="legend-label">{{ row.label }}</span>
    </li>
  </ul>
</template>

<style scoped>
.calendar-legend { list-style: none; margin: 0; padding: 0; }
.legend-row { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
.legend-swatch { flex: 0 0 48px; transform: scale(0.85); }
.legend-label { font-size: 14px; }
</style>
